import type { TranslationKeys } from '@vocab/core';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Bleed,
  Box,
  Inline,
  Stack,
  Tag,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import type { ComponentProps, FC } from 'react';

import { PriorityApplyBadge } from 'src/App/AppPageBlock/PriorityApplyBadge';
import { SEEKPassAlert } from 'src/App/AppPageBlock/SEEKPass';
import { useFeatureFlag } from 'src/App/CandidateManagementPage/FeatureFlagsContext';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { AttachmentTypeName } from 'src/constants/attachmentTypes';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import { TrackOnMount } from 'src/libs/Analytics';
import { Display } from 'src/libs/Display';
import { downloadFile } from 'src/libs/fileDownload';
import {
  trackAlertDismissEvent,
  trackDownloadDocumentEvent,
} from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import { ApplicationQuestions } from '../ApplicationQuestions';
import { Education } from '../Education';
import { Licence } from '../Licence';
import { ProfileDetails } from '../ProfileDetails';
import { ProfileSection } from '../ProfileSection';
import { Role } from '../Role';
import { VerifiedCredentials } from '../VerifiedCredentials';

import translations from './.vocab';
import { HighFitCalloutDesktop } from './HighFitCalloutDesktop';
import { HighFitCalloutMobile } from './HighFitCalloutMobile';
import { ProfileBodyLayout } from './ProfileBodyLayout';

export type AttachmentType =
  | 'RESUME'
  | 'COVER_LETTER'
  | 'REFERENCE_CHECK'
  | 'SELECTION_CRITERIA'
  | 'OTHER';

const getLabelFromAttachmentType = (
  type: AttachmentType,
): TranslationKeys<typeof translations> => {
  switch (type) {
    case 'RESUME':
      return 'Resumé';
    case 'COVER_LETTER':
      return 'Cover Letter';
    case 'SELECTION_CRITERIA':
      return 'Selection Criteria';
    case 'REFERENCE_CHECK':
      return 'Reference/s ({count})';
    case 'OTHER':
      return 'Cover Letter';
  }
};

interface ProfileBodyProps {
  applicationDocuments: {
    attachments: Array<{
      attachmentId: string;
      type: AttachmentType;
      filename: string | null;
      typeName: AttachmentTypeName | undefined;
      downloadUrl: string;
    }>;
    tracking: {
      applicationCorrelationId: string;
      applicationId: string;
      seekCandidateId: string;
      adcentreCandidateId: string;
      prospectId: string;
      isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
    };
  };
  roles: Array<ComponentProps<typeof Role>>;
  educationItems: Array<ComponentProps<typeof Education>>;
  verifiedIdentity: {
    viewFields: Array<{
      label: string;
      value: string[];
    }>;
  } | null;
  licences: Array<ComponentProps<typeof Licence>>;
  employmentStatus: {
    nationalities: string[];
    rightsToWork: string[];
  };
  applicationQuestions: ComponentProps<typeof ApplicationQuestions>;
  skills: string[];
  referenceChecksCount: number;
  candidateFullName: string;
  jobApplicationPositionTitle: string;
  shouldShowVerifiedCredentials?: boolean;
  showCareerHistoryVerificationsAlert: boolean;
  shouldShowPriorityApply: boolean;
  highFitSummary: string | null;
  onClickVerificationsAlertLink: () => void;
  onClickProfileTabVerificationsLearnMore: () => void;
  onVerificationsAlertMount: () => void;
}

export const ProfileBody: FC<ProfileBodyProps> = ({
  applicationDocuments,
  roles,
  educationItems,
  verifiedIdentity,
  licences,
  employmentStatus,
  referenceChecksCount,
  applicationQuestions,
  skills,
  candidateFullName,
  jobApplicationPositionTitle,
  shouldShowVerifiedCredentials,
  showCareerHistoryVerificationsAlert,
  shouldShowPriorityApply,
  highFitSummary,
  onClickVerificationsAlertLink,
  onClickProfileTabVerificationsLearnMore,
  onVerificationsAlertMount,
}) => {
  const { t } = useTranslations(translations);

  const { getCertsyVerifiedFullNameAlert, hideCertsyVerifiedFullNameAlert } =
    useUserActionStatus('certsy-verified-full-name-alert');

  const { variant } = useFeatureFlag('cm_high_fit_applicant_explainability');
  const showHighFit = variant === 'challenger' && highFitSummary;

  const { site } = useAppConfig();

  const shouldShowNationalities = employmentStatus.nationalities.length > 0;
  const shouldShowRightsToWork =
    (site === 'employer-seek-my' ||
      site === 'employer-seek-sg' ||
      site === 'employer-seek-hk' ||
      site === 'employer-seek-th') &&
    employmentStatus.rightsToWork.length > 0;

  return (
    <ProfileBodyLayout
      highFitSummary={
        showHighFit ? (
          <>
            <Display tablet desktop>
              <HighFitCalloutDesktop highFitSummary={highFitSummary} />
            </Display>

            <Display mobile>
              <Stack space="small">
                <HighFitCalloutMobile highFitSummary={highFitSummary} />
              </Stack>
            </Display>
          </>
        ) : null
      }
      applicationAttachments={
        applicationDocuments.attachments.length > 0 ? (
          <Display mobile>
            <ProfileSection title={t('Application documents')}>
              <Stack space="small">
                {applicationDocuments.attachments.map((attachment) => {
                  const isReferenceCheck =
                    attachment.type === 'REFERENCE_CHECK';
                  const shouldTranslateLabel =
                    isReferenceCheck || !attachment.filename;

                  const attachmentLabel = shouldTranslateLabel
                    ? t(
                        getLabelFromAttachmentType(attachment.type),
                        isReferenceCheck
                          ? { count: referenceChecksCount }
                          : undefined,
                      )
                    : attachment.filename;

                  const onClickAttachment = async () => {
                    await downloadFile(attachment.downloadUrl, () => {});
                    trackDownloadDocumentEvent({
                      applicationCorrelationId:
                        applicationDocuments.tracking.applicationCorrelationId,
                      applicationId:
                        applicationDocuments.tracking.applicationId,
                      attachmentId: attachment.attachmentId,
                      seekCandidateId:
                        applicationDocuments.tracking.seekCandidateId,
                      adcentreCandidateId:
                        applicationDocuments.tracking.adcentreCandidateId,
                      prospectId: applicationDocuments.tracking.prospectId,
                      documentType: attachment.typeName,
                      currentPage: TRACKING_CANDIDATE_DETAILS,
                      isVerifiedId: applicationDocuments.tracking.isVerifiedId,
                    });
                  };

                  return (
                    <Box
                      key={attachment.attachmentId}
                      style={{ overflowWrap: 'break-word' }}
                    >
                      <Text baseline={false} size="small">
                        <TextLinkButton onClick={onClickAttachment}>
                          {attachmentLabel}
                        </TextLinkButton>
                      </Text>
                    </Box>
                  );
                })}
              </Stack>
            </ProfileSection>
          </Display>
        ) : null
      }
      priorityApplyInformation={
        shouldShowPriorityApply ? (
          <Bleed left="large">
            <Box
              style={{ borderLeft: '4px solid #E1B2F5' }}
              paddingLeft="medium"
              paddingTop="xxsmall"
              borderRadius="small"
              alignItems="center"
            >
              <PriorityApplyBadge variant="badge" />
              <Box paddingY="small">
                <Text size="small">
                  {t(
                    '{candidateName} has identified {position} as one of their top three choices this month, showcasing their strong motivation to pursue this opportunity. We recommend contacting {candidateName} to find out more.',
                    {
                      candidateName: candidateFullName,
                      position: jobApplicationPositionTitle,
                    },
                  )}
                </Text>
              </Box>
            </Box>
          </Bleed>
        ) : null
      }
      careerHistory={
        roles.length > 0 || showCareerHistoryVerificationsAlert ? (
          <ProfileSection title={t('Career history')}>
            {showCareerHistoryVerificationsAlert ? (
              <TrackOnMount onMount={onVerificationsAlertMount}>
                <SEEKPassAlert iconSize="small">
                  <Text size="small">
                    {t(
                      'See career history verified by SEEK Pass in the <Link>Verifications tab</Link>.',
                      {
                        Link: (children) => (
                          <TextLinkButton
                            onClick={onClickVerificationsAlertLink}
                          >
                            {children}
                          </TextLinkButton>
                        ),
                      },
                    )}
                  </Text>
                </SEEKPassAlert>
              </TrackOnMount>
            ) : null}
            <Stack space="xlarge">
              {roles.map((role, index) => (
                <Role
                  key={index}
                  title={role.title}
                  company={role.company}
                  from={role.from}
                  to={role.to}
                  achievements={role.achievements}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null
      }
      educationHistory={
        educationItems.length > 0 ? (
          <ProfileSection title={t('Education')}>
            <Stack space="large">
              {educationItems.map((education) => (
                <Education
                  key={education.id}
                  id={education.id}
                  title={education.title}
                  institute={education.institute}
                  completed={education.completed}
                  completionDate={education.completionDate}
                  highlights={education.highlights}
                  verificationViewFields={education.verificationViewFields}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null
      }
      licencesAndCertification={
        licences.length > 0 ? (
          <ProfileSection title={t('Licences and certifications')}>
            <Stack space="large">
              {licences.map((licence) => (
                <Licence
                  key={licence.id}
                  id={licence.id}
                  title={licence.title}
                  issuingOrganisation={licence.issuingOrganisation}
                  formattedDate={licence.formattedDate}
                  description={licence.description}
                  verificationViewFields={licence.verificationViewFields}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null
      }
      digitalId={
        verifiedIdentity ? (
          <ProfileSection
            title={t('Digital ID')}
            tooltip={t(
              'A verified identity means this person has confirmed their identity with the SEEK Pass verification platform using a government-issued document.',
            )}
          >
            <Stack space="small">
              <ProfileDetails
                details={verifiedIdentity.viewFields.map(
                  (viewField, index) => ({
                    name: viewField.label,
                    values: viewField.value,
                    showVerifiedIcon: index === 0,
                  }),
                )}
              />
              {getCertsyVerifiedFullNameAlert() === 'visible' && (
                <Alert
                  tone="info"
                  onClose={() => {
                    trackAlertDismissEvent({
                      alertShownMessageCode:
                        'Candidate Management - Verified ID Banner',
                    });
                    hideCertsyVerifiedFullNameAlert();
                  }}
                  closeLabel={t('Close')}
                >
                  <Text size="small">
                    {t(
                      'A verified identity means this person has confirmed their identity with the SEEK Pass verification platform using a government-issued document. This may differ from their SEEK profile name as they may be using a preferred name.',
                    )}
                  </Text>
                </Alert>
              )}
            </Stack>
          </ProfileSection>
        ) : null
      }
      employementStatus={
        shouldShowNationalities || shouldShowRightsToWork ? (
          <ProfileSection title={t('Employment status')}>
            <Stack space="medium">
              {shouldShowNationalities ? (
                <ProfileDetails
                  details={[
                    {
                      name: t('Nationality'),
                      values: employmentStatus.nationalities,
                    },
                  ]}
                />
              ) : null}

              {shouldShowRightsToWork ? (
                <ProfileDetails
                  details={[
                    {
                      name: t('Right to work'),
                      values: employmentStatus.rightsToWork,
                    },
                  ]}
                />
              ) : null}
            </Stack>
          </ProfileSection>
        ) : null
      }
      verifiedCredentials={
        shouldShowVerifiedCredentials ? (
          <VerifiedCredentials
            onClickLearnHowCredentialsAreVerified={
              onClickProfileTabVerificationsLearnMore
            }
          />
        ) : null
      }
      applicationQuestions={
        applicationQuestions.questions.length > 0 ? (
          <ProfileSection title={t('Application questions')}>
            <ApplicationQuestions
              questions={applicationQuestions.questions}
              allVerifications={applicationQuestions.allVerifications}
            />
          </ProfileSection>
        ) : null
      }
      skills={
        skills.length > 0 ? (
          <ProfileSection title={t('Skills')}>
            <Inline space="small">
              {skills.map((skill) => (
                <Tag key={skill} size="small">
                  {skill}
                </Tag>
              ))}
            </Inline>
          </ProfileSection>
        ) : null
      }
    />
  );
};
